import { Info } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { BodySecondary, H2, LabelMini } from '@liveauctioneers/hammer-ui-core/text';
import { Tooltip } from '@liveauctioneers/hammer-ui-core/tooltip';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useEffect } from 'react';

import { CurrencySelection } from '@/components/CurrencySelection/CurrencySelection';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useIsTabletLayout } from '@/hooks/useIsTabletLayout';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { openExcludeHouseModal } from '@/redux/modules/modal';
import { getUserExcludedHouses } from '@/redux/modules/search/exclusions/searchExclusions.selectors';
import { fetchSellersByIdsIfNeeded } from '@/redux/modules/seller/seller.actions';
import { getHousesById } from '@/redux/modules/seller/seller.selectors';
import { Seller as SellerType } from '@/types/Seller';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getSellerUrl } from '@/utils/urls';

export const PreferencesForm = () => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const isTablet = useIsTabletLayout();
    const excludedHouseIds = useAppSelector(getUserExcludedHouses);
    const excludedHouses = useAppSelector((state) => getHousesById(state, excludedHouseIds));

    const showConfirmExcludeModal = useCallback(
        (houseId: number) => {
            dispatch(openExcludeHouseModal(houseId, true));
        },
        [dispatch]
    );
    const hasExcludedHouses = Boolean(Array.isArray(excludedHouses) && excludedHouses.length);

    useEffect(() => {
        if (excludedHouseIds.length) {
            dispatch(fetchSellersByIdsIfNeeded(excludedHouseIds));
        }
    }, [dispatch, excludedHouseIds]);

    const sellerRows = excludedHouses.map((seller: SellerType) => (
        <div key={seller.sellerId}>
            <div className="flex flex-row items-center py-xs">
                <BodySecondary className="flex-1">
                    <RouteLink
                        className="no-underline [&:hover]:text-primary-400 [&:hover]:underline"
                        to={getSellerUrl(seller.sellerId, seller.name)}
                    >
                        {seller.name}
                    </RouteLink>
                </BodySecondary>
                <div className="w-[80px]">
                    <InlineButton
                        onClick={() => showConfirmExcludeModal(seller.sellerId)}
                        size="sm"
                    >
                        {formatMessage(['include'])}
                    </InlineButton>
                </div>
            </div>
            <div className="border-b border-border-secondary" />
        </div>
    ));

    return (
        <div className={cn('mb-lg ml-md', isTablet ? 'mr-xs w-full' : 'w-1/2')}>
            {hasExcludedHouses && (
                <div className="flex flex-col">
                    <H2 className="mt-2xs">{formatMessage(['excluded_houses'])}</H2>
                    <BodySecondary className="mt-xs">{formatMessage(['you_excluded_these'])}</BodySecondary>
                    <div className="mt-md flex flex-row items-center">
                        <LabelMini className="flex-1">{formatMessage(['name'])}</LabelMini>
                        <div className="w-[80px]">
                            <LabelMini>{formatMessage(['include'])}</LabelMini>
                        </div>
                    </div>
                    {/* border-border-primary is closest to grey200 (#8b8b8b) */}
                    <div className="border-b border-border-primary" />
                </div>
            )}
            {sellerRows}
            <H2
                className={cn('mb-lg flex items-center gap-2xs', hasExcludedHouses ? 'mt-2xl' : 'mt-lg')}
                data-testid="sectionTitle"
            >
                {formatMessage(['preferredCurrencyConversion', 'changeCurrency'])}
                <Info
                    className="ml-2xs inline-block text-surface-500"
                    id="preferences-form-currency-info-trigger"
                    size="sm"
                    style="fill"
                />
                <Tooltip anchorSelect="#preferences-form-currency-info-trigger">
                    <BodySecondary>{formatMessage(['preferredCurrencyConversion', 'popoverSummary'])}</BodySecondary>
                </Tooltip>
            </H2>
            <CurrencySelection />
        </div>
    );
};

export default PreferencesForm;
